import * as React from "react";
import { Bell, ChevronDown } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { useMdViewport } from "../../lib/useMdViewport";
import type { Pathway } from "../../lib/learning/types";

function defaultDueLabel(count: number, planTitle: string): string {
  return `${count} topic${count === 1 ? "" : "s"} due on ${planTitle}`;
}

function defaultSwitchLabel(planTitle: string): string {
  return `Change plan — studying ${planTitle}`;
}

export function PlanPicker({
  plans,
  selectedId,
  onSelect,
  label,
  dueByPlanId = {},
  formatDueLabel = defaultDueLabel,
  formatSwitchLabel = defaultSwitchLabel,
}: {
  plans: Pathway[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label: string;
  /** Due recall cards on other plans (not the selected one). */
  dueByPlanId?: Record<string, number>;
  /**
   * Supplied by the caller so this stays presentational — it renders in tests
   * that have no query client, and the locale hook needs one.
   */
  formatDueLabel?: (count: number, planTitle: string) => string;
  formatSwitchLabel?: (planTitle: string) => string;
}) {
  const wide = useMdViewport();
  const [expanded, setExpanded] = React.useState(false);

  if (plans.length <= 1) return null;

  /* Below md the chips wrap to one per row: six plans ate 38% of a phone
     viewport before the learner reached any content. Collapse to the plan
     they're on and let them open the list when they want to move. */
  const collapsible = !wide;
  const open = wide || expanded;
  const selected = plans.find((p) => p.id === selectedId) ?? null;

  const dueElsewhereTotal = plans.reduce(
    (sum, plan) => (plan.id === selectedId ? sum : sum + (dueByPlanId[plan.id] ?? 0)),
    0,
  );

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">{label}</p>

      {collapsible && selected && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls="plan-picker-list"
          aria-label={formatSwitchLabel(selected.title)}
          className={cn(
            "border-primary bg-primary/10 text-primary flex w-full items-center gap-1.5",
            "rounded-full border py-1.5 pl-3 pr-2 text-xs font-semibold",
            "focus-visible:ring-primary/50 focus-visible:outline-none focus-visible:ring-2",
          )}
        >
          <span className="truncate">{selected.title}</span>
          {dueElsewhereTotal > 0 && !expanded && <DueBadge count={dueElsewhereTotal} />}
          <ChevronDown
            aria-hidden
            className={cn(
              "ml-auto size-3.5 shrink-0 transition-transform duration-150",
              expanded && "rotate-180",
            )}
          />
        </button>
      )}

      {open && (
        <div id="plan-picker-list" className="flex flex-wrap gap-2" role="group" aria-label={label}>
          {plans.map((plan) => {
            const active = plan.id === selectedId;
            const dueCount = dueByPlanId[plan.id] ?? 0;
            const dueElsewhere = !active && dueCount > 0;
            const dueLabel = dueElsewhere ? formatDueLabel(dueCount, plan.title) : undefined;

            return (
              <button
                key={plan.id}
                type="button"
                onClick={() => {
                  onSelect(plan.id);
                  setExpanded(false);
                }}
                aria-pressed={active}
                /* The count rides inside the chip rather than floating above it.
                   An absolutely-positioned badge was clipped by the chip's own
                   `truncate` and collided with the row above once chips wrapped. */
                aria-label={dueLabel}
                title={dueLabel}
                className={cn(
                  "flex max-w-full items-center gap-1.5 rounded-full border py-1.5 text-xs font-semibold",
                  "md:max-w-[18rem]",
                  "transition-colors duration-150",
                  "focus-visible:ring-primary/50 focus-visible:outline-none focus-visible:ring-2",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border/60 text-muted-foreground hover:border-primary/30 hover:bg-muted/40",
                  dueElsewhere ? "pl-3 pr-1.5" : "px-3",
                )}
              >
                <span className="truncate">{plan.title}</span>
                {dueElsewhere && <DueBadge count={dueCount} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DueBadge({ count }: { count: number }) {
  return (
    <span
      aria-hidden
      className={cn(
        "bg-joy-amber shrink-0",
        "inline-flex min-w-[1.25rem] items-center justify-center gap-0.5",
        "rounded-full px-1.5 py-0.5 text-[0.6rem] font-bold tabular-nums leading-none",
        "text-white",
      )}
    >
      <Bell className="size-2.5" />
      {count > 9 ? "9+" : count}
    </span>
  );
}
